import Backbone from 'backbone'
import loadingDots from './loading-dots.hbs'
import busyButtonCheckmark from './busy-button-checkmark.hbs'
import $ from 'jquery'

/**
 * Usage:
 *
 * var button = new BusyButton({ el }).loading()
 *
 * If the operation completed successfully, a *done animation* will be played
 * and the button will return to the disabled state by calling:
 *
 * button.done()
 *
 * If the button should be *disabled* without playing the done animation, call:
 *
 * button.disable()
 *
 * If the button should be *enabled* without playing the done animation, call:
 *
 * button.enable()
 *
 * To toggle whether the button is enabled or enabled, you can call:
 *
 * button.toggle(whetherToShowTheButton)
 *
 * If the checkmark should remain when this.done() is called, pass in
 * `keepCheckmark: true` as an option when creating the button
 */
var BusyButton = Backbone.View.extend({

	// states: 'ready', 'disabled', 'loading', and 'done'

	initialize: function(options) {
		this.options = options || {}
		this.state = 'ready'
		// form-view can re-creates busy buttons on top of existing ones so make
		// sure the correct state is recorded
		if (this.$el.hasClass('is-disabled')) {
			this.state = 'disabled'
		}
		if (this.$el.hasClass('is-loading')) {
			this.state = 'loading'
		}
		if (this.$el.hasClass('is-done')) {
			this.state = 'done'
		}
		this.$el.data('view', this)
	},

	_moveToDisabledOrReady: function() {
		if(this.state == 'loading' || this.state == 'done') {
			this._removeExtraMarkup()
			this.$el.removeClass('is-loading is-done')
		}
	},

	_removeExtraMarkup: function() {
		this.$('.holder').remove()
		this.$('.check-holder').remove()
		var original = this.$('span.label').html()
		if(original) {
			// reset the inner text to be what it was before we modified the button
			this.$el.html(original)
		}
	},

	_addExtraMarkup: function() {
		// wrap the content in a span so that we can style it not to show
		this.$el.html('<span class="label">' + this.$el.html() + '</span>')

		// add the dots and check holder
		this.$el.append(loadingDots(), busyButtonCheckmark())
	},

	// enables or disables the button based on the given parameter
	toggle: function(enable) {
		// the only time you can't click on the button is when it is loading
		this.$el.attr('disabled', false)
		this._moveToDisabledOrReady()

		this.$el.toggleClass('is-disabled', !enable)
		this.state = enable ? 'ready' : 'disabled'

		return this
	},

	/*
	 * Changes the button the ready state. Does not play the "done" animation.
	 */
	enable: function() {
		return this.toggle(true)
	},

	/*
	 * Changes the button to the 'disabled' state. The button text is shown but
	 * the button is not clickable.
	*/
	disable: function() {
		return this.toggle(false)
	},

	/**
	 * Returns true if the button is visually disabled. Note that we don't set the `disabled` property even it's
	 * visually disabled, so click events are still fired.
	 */
	disabled: function () {
		return this.$el.hasClass('is-disabled')
	},

	/*
	 * Changes to the done state, plays the "done" animation, then moves to
	 * the 'disabled' state.  Triggers an animation-complete event once the
	 * animation is complete
	*/
	done: function(d) {
		var deferred = $.Deferred()
		this.$el.addClass('is-done')
		// the only time you can't click on the button is when it is loading
		this.$el.attr('disabled', false)
		this.$('.check-holder').one('transitionend', function() {
			if (this.state == 'done') {
				this.disable()
			}
			this.trigger('animation-complete')
			deferred.resolve()
		}.bind(this))

		// Prevent the done state if the checkmark should remain
		if (this.options.keepCheckmark) {
			return
		}

		this.state = 'done'
		return deferred
	},

	/*
	 * Changes to the 'loading' state where the button is disabled, the button
	 * text is not shown, and a three dot loading animation is shown.
	*/
	loading: function() {
		// don't allow interacting with the button further while waiting for a response from the server
		this.$el.attr('disabled', true)
		this.$el.addClass('is-loading')
		this._addExtraMarkup()
		this.state = 'loading'

		return this
	},

	/**
	 * Shows the "checkmark slides in, then goes away" animation from done(), but works
	 * when loading() wasn't previously called
	 *
	 * @param {Boolean} [enableAfterDone=false] if true, will enable the button
	 * after the done() method is called.  The default behavior will leave the
	 * button in a disabled state after being done.
	 */
	instadone: function (enableAfterDone) {
		this.enable()
		this.loading()
		let deferred = $.Deferred()
		// wait 10ms so that we're sure there's no timing conflicts since things
		// are sensitive here with the transitions.
		setTimeout(() => {
			$.when(this.done(), () => deferred.resolve())
			if (enableAfterDone) {
				this.enable()
			}
		}, 10)
		return deferred
	}

})

export default BusyButton

BusyButton.__filename__ = 'components/busy-button.js'
